import React, { useState, useEffect, useMemo } from 'react';
import { JoySenseService } from '../../services/backend-api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFilters } from '../../contexts/FilterContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

interface UmbralesPorLoteProps {}

interface LoteUmbralData {
  localizacionid: number;
  localizacion: string;
  umbralesPorTipo: { [tipoid: number]: { minimo: number; maximo: number; umbral: string; criticidadid: number } };
  umbralCount: number;
}

const UmbralesPorLote: React.FC<UmbralesPorLoteProps> = () => {
  const { t } = useLanguage();
  const { setShowDetailedAnalysis } = useFilters();
  const [metricas, setMetricas] = useState<any[]>([]);
  const [fundos, setFundos] = useState<any[]>([]);
  const [localizaciones, setLocalizaciones] = useState<any[]>([]);
  const [tipos, setTipos] = useState<any[]>([]);
  const [criticidades, setCriticidades] = useState<any[]>([]);
  const [selectedFundos, setSelectedFundos] = useState<number[]>([]);
  const [selectedMetrica, setSelectedMetrica] = useState<number | null>(null);
  const [orden, setOrden] = useState<'asc' | 'desc'>('desc');
  const [lotesData, setLotesData] = useState<LoteUmbralData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFundoDropdownOpen, setIsFundoDropdownOpen] = useState(false);
  const [isMetricaDropdownOpen, setIsMetricaDropdownOpen] = useState(false);
  const [selectedLote, setSelectedLote] = useState<LoteUmbralData | null>(null);
  const [selectedLotes, setSelectedLotes] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [detalleExpanded, setDetalleExpanded] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [metricasData, fundosData, tiposData, criticidadesData] = await Promise.all([
          JoySenseService.getMetricas(),
          JoySenseService.getFundos(),
          JoySenseService.getTipos(),
          JoySenseService.getTableData('criticidad', 1000)
        ]);
        
        setMetricas(metricasData || []);
        setFundos(fundosData || []);
        setTipos(tiposData || []);
        setCriticidades(criticidadesData || []);
      } catch (err: any) {
        console.error('Error cargando datos iniciales:', err);
        setError('Error al cargar métricas y fundos');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Resetear el estado de análisis detallado cuando el dashboard se monta
  useEffect(() => {
    setShowDetailedAnalysis(false);
  }, [setShowDetailedAnalysis]);

  // Cargar localizaciones cuando se seleccionan fundos
  useEffect(() => {
    const loadLocalizaciones = async () => {
      if (selectedFundos.length === 0) {
        setLocalizaciones([]);
        return;
      }

      try {
        // Obtener localizaciones que pertenecen a nodos de los fundos seleccionados
        const localizacionesData = await JoySenseService.getLocalizaciones();
        // Filtrar localizaciones por fundos seleccionados (a través de nodos -> ubicaciones -> fundos)
        const nodosData = await JoySenseService.getNodos();
        const ubicacionesData = await JoySenseService.getUbicaciones();
        
        // Crear un mapa de nodoid -> fundoid
        const nodoToFundoMap = new Map<number, number>();
        nodosData.forEach((nodo: any) => {
          const ubicacion = ubicacionesData.find((u: any) => u.ubicacionid === nodo.ubicacionid);
          const fundoId = ubicacion?.zona?.fundoid;
          if (ubicacion && fundoId && selectedFundos.includes(fundoId)) {
            nodoToFundoMap.set(nodo.nodoid, fundoId);
          }
        });
        
        // Filtrar localizaciones que pertenecen a nodos de los fundos seleccionados
        const localizacionesFiltradas = localizacionesData.filter((loc: any) => {
          return nodoToFundoMap.has(loc.nodoid);
        });
        
        setLocalizaciones(localizacionesFiltradas);
      } catch (err: any) {
        console.error('Error cargando localizaciones:', err);
        setLocalizaciones([]);
      }
    };

    loadLocalizaciones();
  }, [selectedFundos]);

  // Calcular umbrales por lote
  const calcularUmbralesPorLote = async () => {
    if (selectedFundos.length === 0 || !selectedMetrica) {
      setLotesData([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Obtener umbrales por lote
      const umbrales = await JoySenseService.getUmbralesPorLote({
        fundoIds: selectedFundos,
        metricaId: selectedMetrica
      });

      // Agrupar por localización
      const loteMap = new Map<number, { 
        localizacion: string; 
        umbralesPorTipo: { [tipoid: number]: { minimo: number; maximo: number; umbral: string; criticidadid: number } };
        umbralCount: number;
      }>();

      umbrales.forEach((umbral: any) => {
        // Obtener localizacionid desde múltiples fuentes posibles
        const localizacionId = umbral.localizacionid || umbral.localizacion?.localizacionid || null;
        // Obtener tipoid desde múltiples fuentes posibles
        const tipoid = umbral.tipoid || umbral.localizacion?.sensor?.tipoid || null;
        // Obtener criticidadid desde múltiples fuentes posibles
        const criticidadId = umbral.criticidadid || umbral.criticidad?.criticidadid || null;

        if (!localizacionId || !tipoid) {
          return;
        }

        if (!loteMap.has(localizacionId)) {
          const localizacion = localizaciones.find(loc => loc.localizacionid === localizacionId);
          loteMap.set(localizacionId, {
            localizacion: localizacion?.localizacion || `Localización ${localizacionId}`,
            umbralesPorTipo: {},
            umbralCount: 0
          });
        }

        const lote = loteMap.get(localizacionId)!;
        // Contar umbrales por localización (incrementar contador)
        lote.umbralCount = lote.umbralCount + 1;
        lote.umbralesPorTipo[tipoid] = {
          minimo: umbral.minimo,
          maximo: umbral.maximo,
          umbral: umbral.umbral,
          criticidadid: criticidadId
        };
      });

      // Crear array de datos
      const lotesArray: LoteUmbralData[] = Array.from(loteMap.entries()).map(([localizacionid, data]) => {
        return {
          localizacionid,
          localizacion: data.localizacion,
          umbralesPorTipo: data.umbralesPorTipo,
          umbralCount: data.umbralCount
        };
      });

      // Ordenar según el orden seleccionado (usar promedio de mínimos para ordenar)
      lotesArray.sort((a, b) => {
        const minimosA = Object.values(a.umbralesPorTipo).map(u => u.minimo);
        const minimosB = Object.values(b.umbralesPorTipo).map(u => u.minimo);
        const promedioMinA = minimosA.length > 0 ? minimosA.reduce((sum, val) => sum + val, 0) / minimosA.length : 0;
        const promedioMinB = minimosB.length > 0 ? minimosB.reduce((sum, val) => sum + val, 0) / minimosB.length : 0;
        
        if (orden === 'desc') {
          return promedioMinB - promedioMinA;
        } else {
          return promedioMinA - promedioMinB;
        }
      });

      setLotesData(lotesArray);
    } catch (err: any) {
      console.error('Error calculando umbrales por lote:', err);
      setError('Error al calcular umbrales por lote');
      setLotesData([]);
    } finally {
      setLoading(false);
    }
  };

  // Recalcular cuando cambian los filtros
  useEffect(() => {
    if (selectedFundos.length > 0) {
      calcularUmbralesPorLote();
    } else {
      setLotesData([]);
    }
  }, [selectedMetrica, orden, selectedFundos, localizaciones]);

  // Manejar selección de fundos
  const handleFundoToggle = (fundoId: number) => {
    setSelectedFundos(prev => {
      if (prev.includes(fundoId)) {
        return prev.filter(id => id !== fundoId);
      } else {
        return [...prev, fundoId];
      }
    });
  };

  // Manejar "Seleccionar todos"
  const handleSelectAllFundos = () => {
    if (selectedFundos.length === fundos.length) {
      setSelectedFundos([]);
    } else {
      setSelectedFundos(fundos.map(f => f.fundoid));
    }
  };

  // Manejar selección de lote (checkbox)
  const handleLoteToggle = (localizacionId: number, e?: React.SyntheticEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSelectedLotes(prev => {
      if (prev.includes(localizacionId)) {
        return prev.filter(id => id !== localizacionId);
      } else {
        return [...prev, localizacionId];
      }
    });
  };

  // Manejar click en fila de tabla (abrir modal)
  const handleRowClick = (lote: LoteUmbralData) => {
    if (selectedLotes.length === 0) {
      // Si no hay selección, seleccionar este lote y abrir modal
      setSelectedLotes([lote.localizacionid]);
      setSelectedLote(lote);
      setShowModal(true);
    } else {
      // Si hay selección, abrir modal con todos los seleccionados
      const lotesSeleccionados = lotesData.filter(l => selectedLotes.includes(l.localizacionid));
      if (lotesSeleccionados.length === 1) {
        setSelectedLote(lotesSeleccionados[0]);
      } else {
        setSelectedLote(null);
      }
      setShowModal(true);
    }
  };

  // Abrir modal de comparación
  const handleOpenComparison = () => {
    if (selectedLotes.length === 0) return;
    const lotesSeleccionados = lotesData.filter(l => selectedLotes.includes(l.localizacionid));
    if (lotesSeleccionados.length === 1) {
      setSelectedLote(lotesSeleccionados[0]);
    } else {
      setSelectedLote(null);
    }
    setShowModal(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLote(null);
    setDetalleExpanded(false);
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-black min-h-screen">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-6">
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Combobox Fundo - Selección Múltiple */}
          <div className="relative">
            <label className="block text-sm font-medium text-blue-500 mb-2 font-mono tracking-wider">
              FUNDO
            </label>
            <button
              type="button"
              onClick={() => {
                setIsFundoDropdownOpen(!isFundoDropdownOpen);
                setIsMetricaDropdownOpen(false);
              }}
              className="w-full px-3 py-2 bg-gray-100 dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-gray-900 dark:text-white font-mono text-left flex items-center justify-between hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
            >
              <span>
                {selectedFundos.length === 0
                  ? 'Seleccionar fundo'
                  : selectedFundos.length === 1
                  ? fundos.find(f => f.fundoid === selectedFundos[0])?.fundo || 'Seleccionar fundo'
                  : `${selectedFundos.length} fundos seleccionados`}
              </span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isFundoDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto dashboard-scrollbar-blue">
                <button
                  onClick={handleSelectAllFundos}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors font-mono border-b border-gray-200 dark:border-neutral-600 ${
                    selectedFundos.length === fundos.length
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {selectedFundos.length === fundos.length ? '✓ Seleccionar todos' : 'Seleccionar todos'}
                </button>
                {fundos.map((fundo) => (
                  <button
                    key={fundo.fundoid}
                    onClick={() => handleFundoToggle(fundo.fundoid)}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors font-mono flex items-center gap-2 ${
                      selectedFundos.includes(fundo.fundoid)
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    <span className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                      selectedFundos.includes(fundo.fundoid)
                        ? 'border-white bg-white'
                        : 'border-gray-400 dark:border-gray-500'
                    }`}>
                      {selectedFundos.includes(fundo.fundoid) && (
                        <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                    {fundo.fundo}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Combobox Métrica */}
          <div className="relative">
            <label className="block text-sm font-medium text-blue-500 mb-2 font-mono tracking-wider">
              MÉTRICA
            </label>
            <button
              type="button"
              onClick={() => {
                setIsMetricaDropdownOpen(!isMetricaDropdownOpen);
                setIsFundoDropdownOpen(false);
              }}
              className="w-full px-3 py-2 bg-gray-100 dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-gray-900 dark:text-white font-mono text-left flex items-center justify-between hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
            >
              <span>{selectedMetrica ? metricas.find(m => m.metricaid === selectedMetrica)?.metrica || 'Seleccionar métrica' : 'Seleccionar métrica'}</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isMetricaDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto dashboard-scrollbar-blue">
                {metricas.map((metrica) => (
                  <button
                    key={metrica.metricaid}
                    onClick={() => {
                      setSelectedMetrica(metrica.metricaid);
                      setIsMetricaDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors font-mono ${
                      selectedMetrica === metrica.metricaid
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {metrica.metrica}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Orden - Toggle Switch */}
          <div>
            <label className="block text-sm font-medium text-blue-500 mb-2 font-mono tracking-wider">
              ORDEN
            </label>
            <div className="relative w-full max-w-[120px]">
              <button
                type="button"
                onClick={() => setOrden(orden === 'desc' ? 'asc' : 'desc')}
                className="relative w-full h-9 bg-gray-200 dark:bg-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 transition-colors"
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-[calc(50%-0.125rem)] h-8 bg-blue-600 rounded-md transition-all duration-300 ease-in-out ${
                    orden === 'asc' ? 'translate-x-full' : 'translate-x-0'
                  }`}
                />
                <div className="relative flex h-full items-center justify-around font-mono text-xs font-bold px-1">
                  <span className={`z-10 transition-colors duration-300 ${
                    orden === 'desc' ? 'text-white' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    Desc
                  </span>
                  <span className={`z-10 transition-colors duration-300 ${
                    orden === 'asc' ? 'text-white' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    Asc
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-900 border border-red-600 rounded-md">
            <p className="text-sm text-red-300 font-mono">{error}</p>
          </div>
        )}

        {/* Botón de comparación */}
        {selectedLotes.length > 0 && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={handleOpenComparison}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-mono text-sm transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Comparar ({selectedLotes.length} {selectedLotes.length === 1 ? 'localización' : 'localizaciones'})
            </button>
          </div>
        )}

        {/* Tabla de Resultados */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-600 dark:text-gray-400 font-mono">Cargando datos...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200 dark:bg-neutral-700">
                  <th className="px-2 py-3 text-center text-sm font-bold text-gray-900 dark:text-white font-mono tracking-wider border-b border-gray-300 dark:border-neutral-600 w-12">
                    {/* Checkbox header */}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 dark:text-white font-mono tracking-wider border-b border-gray-300 dark:border-neutral-600">
                    LOCALIZACIÓN
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 dark:text-white font-mono tracking-wider border-b border-gray-300 dark:border-neutral-600">
                    UMBRAL MIN
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 dark:text-white font-mono tracking-wider border-b border-gray-300 dark:border-neutral-600">
                    UMBRAL MAX
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 dark:text-white font-mono tracking-wider border-b border-gray-300 dark:border-neutral-600">
                    N° UMBRALES
                  </th>
                </tr>
              </thead>
              <tbody>
                {lotesData.length > 0 ? (
                  lotesData.map((lote) => {
                    // Calcular promedios de mínimos y máximos
                    const minimos = Object.values(lote.umbralesPorTipo).map(u => u.minimo);
                    const maximos = Object.values(lote.umbralesPorTipo).map(u => u.maximo);
                    const promedioMin = minimos.length > 0 
                      ? minimos.reduce((sum, val) => sum + val, 0) / minimos.length 
                      : null;
                    const promedioMax = maximos.length > 0 
                      ? maximos.reduce((sum, val) => sum + val, 0) / maximos.length 
                      : null;

                    // Preparar detalle por tipo para el tooltip (promedios por tipo)
                    const detallePorTipo = Object.entries(lote.umbralesPorTipo)
                      .map(([tipoid, data]) => {
                        const tipo = tipos.find(t => t.tipoid === Number(tipoid));
                        return {
                          tipoNombre: tipo?.tipo || `Tipo ${tipoid}`,
                          minimo: data.minimo,
                          maximo: data.maximo
                        };
                      })
                      .sort((a, b) => a.tipoNombre.localeCompare(b.tipoNombre));

                    return (
                      <tr
                        key={lote.localizacionid}
                        onClick={() => handleRowClick(lote)}
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors border-b border-gray-200 dark:border-neutral-600"
                      >
                        <td 
                          className="px-2 py-3 text-center" 
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLotes.includes(lote.localizacionid)}
                            onChange={(e) => {
                              e.stopPropagation();
                              const isChecked = e.target.checked;
                              setSelectedLotes(prev => {
                                if (isChecked) {
                                  return prev.includes(lote.localizacionid) ? prev : [...prev, lote.localizacionid];
                                } else {
                                  return prev.filter(id => id !== lote.localizacionid);
                                }
                              });
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-600 cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                          {lote.localizacion}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono font-bold relative group">
                          {promedioMin !== null ? (
                            <>
                              <span className="cursor-help">{promedioMin.toFixed(2)}</span>
                              {/* Tooltip - Posicionado al costado derecho */}
                              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 bg-gray-900 dark:bg-neutral-800 text-white text-xs rounded-lg shadow-xl z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none p-3">
                                <div className="font-bold mb-2 text-green-400 font-mono border-b border-gray-700 pb-1">
                                  PROMEDIO MIN POR TIPO
                                </div>
                                <div className="space-y-1">
                                  {detallePorTipo.map((detalle, idx) => (
                                    <div key={idx} className="flex justify-between items-center font-mono">
                                      <span className="text-gray-300">{detalle.tipoNombre}:</span>
                                      <span className="font-bold text-white ml-2">{detalle.minimo.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                                {detallePorTipo.length === 0 && (
                                  <div className="text-gray-400 font-mono text-center py-1">
                                    Sin datos
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono font-bold relative group">
                          {promedioMax !== null ? (
                            <>
                              <span className="cursor-help">{promedioMax.toFixed(2)}</span>
                              {/* Tooltip - Posicionado al costado derecho */}
                              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 bg-gray-900 dark:bg-neutral-800 text-white text-xs rounded-lg shadow-xl z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none p-3">
                                <div className="font-bold mb-2 text-green-400 font-mono border-b border-gray-700 pb-1">
                                  PROMEDIO MAX POR TIPO
                                </div>
                                <div className="space-y-1">
                                  {detallePorTipo.map((detalle, idx) => (
                                    <div key={idx} className="flex justify-between items-center font-mono">
                                      <span className="text-gray-300">{detalle.tipoNombre}:</span>
                                      <span className="font-bold text-white ml-2">{detalle.maximo.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                                {detallePorTipo.length === 0 && (
                                  <div className="text-gray-400 font-mono text-center py-1">
                                    Sin datos
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono">
                          {lote.umbralCount}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="border-b border-gray-200 dark:border-neutral-600">
                    <td className="px-2 py-3 text-sm text-gray-500 dark:text-gray-500 font-mono text-center">
                      -
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-500 font-mono text-center">
                      -
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-500 font-mono text-center">
                      -
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-500 font-mono text-center">
                      -
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-500 font-mono text-center">
                      -
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal con gráfico de intervalos */}
      {showModal && (selectedLote || selectedLotes.length > 0) && (() => {
        // Obtener lotes seleccionados
        const lotesSeleccionados = selectedLote 
          ? [selectedLote]
          : lotesData.filter(l => selectedLotes.includes(l.localizacionid));
        
        const isComparacion = lotesSeleccionados.length > 1;

        // Si es un solo lote, usar la lógica original
        if (!isComparacion && selectedLote) {
          // Preparar datos del gráfico: un intervalo por cada tipo de sensor
          const chartData = Object.entries(selectedLote.umbralesPorTipo)
            .map(([tipoid, data]) => {
              const tipo = tipos.find(t => t.tipoid === Number(tipoid));
              return {
                tipo: tipo?.tipo || `Tipo ${tipoid}`,
                min: data.minimo,
                max: data.maximo,
                rango: data.maximo - data.minimo
              };
            })
            .sort((a, b) => a.tipo.localeCompare(b.tipo));

          // Preparar detalle por tipo para mostrar en el modal
        const detallePorTipo = Object.entries(selectedLote.umbralesPorTipo)
          .map(([tipoid, data]) => {
            const tipo = tipos.find(t => t.tipoid === Number(tipoid));
            // Buscar criticidad por ID o por defecto usar 'N/A'
            let criticidadNombre = 'N/A';
            if (data.criticidadid) {
              const criticidad = criticidades.find(c => c.criticidadid === data.criticidadid);
              criticidadNombre = criticidad?.criticidad || 'N/A';
            }
            return {
              tipoNombre: tipo?.tipo || `Tipo ${tipoid}`,
              minimo: data.minimo,
              maximo: data.maximo,
              umbral: data.umbral,
              criticidad: criticidadNombre
            };
          })
          .sort((a, b) => a.tipoNombre.localeCompare(b.tipoNombre));

        // Colores para diferentes tipos de sensores
        const colors = [
          '#10B981', // verde
          '#3B82F6', // azul
          '#F59E0B', // amarillo
          '#EF4444', // rojo
          '#8B5CF6', // púrpura
          '#EC4899', // rosa
          '#06B6D4', // cyan
          '#84CC16', // lime
        ];

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-neutral-900 dark:bg-neutral-900 rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-600 dark:border-gray-600">
                <div>
                  <h2 className="text-xl font-bold text-white dark:text-white font-mono tracking-wider">
                    INTERVALOS DE UMBRALES - {selectedLote.localizacion.toUpperCase()}
                  </h2>
                  <p className="text-sm text-gray-400 dark:text-gray-400 font-mono mt-1">
                    Visualización de rangos de umbrales por tipo de sensor
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Contenido */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Gráfico - Barra horizontal más compacta */}
                {chartData.length > 0 && (
                  <div className="mb-6">
                    <div className="h-64 w-full bg-neutral-800 rounded-lg p-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          layout="vertical"
                          margin={{ top: 10, right: 30, left: 120, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                          <XAxis 
                            type="number" 
                            domain={['auto', 'auto']}
                            stroke="#9CA3AF"
                            style={{ fontSize: '11px' }}
                            tick={{ fill: '#9CA3AF' }}
                          />
                          <YAxis 
                            type="category" 
                            dataKey="tipo" 
                            stroke="#9CA3AF"
                            style={{ fontSize: '11px' }}
                            tick={{ fill: '#9CA3AF' }}
                            width={110}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1F2937',
                              border: '1px solid #374151',
                              borderRadius: '8px',
                              color: '#F3F4F6',
                              padding: '8px 12px'
                            }}
                            cursor={{ 
                              fill: 'rgba(255, 255, 255, 0.1)',
                              stroke: 'rgba(16, 185, 129, 0.3)',
                              strokeWidth: 1
                            }}
                            content={({ active, payload }) => {
                              if (active && payload && payload.length > 0) {
                                const data = payload[0].payload;
                                return (
                                  <div className="font-mono text-sm">
                                    Rango [{data.min.toFixed(2)} - {data.max.toFixed(2)}]
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="min" stackId="a" fill="transparent" />
                          <Bar 
                            dataKey="rango" 
                            stackId="a" 
                            fill="#10B981"
                            fillOpacity={0.6}
                            radius={[0, 4, 4, 0]}
                          >
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Leyenda con colores por tipo de sensor */}
                    <div className="mt-4">
                      <div className="text-xs text-gray-400 dark:text-gray-400 font-mono mb-2 text-center">
                        Intervalos por Tipo de Sensor:
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-300 dark:text-gray-300 font-mono">
                        {chartData.map((entry, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded" 
                              style={{ backgroundColor: colors[index % colors.length], opacity: 0.8 }}
                            ></div>
                            <span>{entry.tipo}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Detalle por tipo - Colapsable */}
                <div className="bg-neutral-800 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setDetalleExpanded(!detalleExpanded)}
                    className="w-full flex justify-between items-center p-4 hover:bg-neutral-700 transition-colors"
                  >
                    <h3 className="text-lg font-bold text-green-400 font-mono tracking-wider">
                      DETALLE POR TIPO DE SENSOR
                    </h3>
                    <svg
                      className={`w-5 h-5 text-green-400 transition-transform ${detalleExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {detalleExpanded && (
                    <div className="p-4 border-t border-gray-700">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {detallePorTipo.map((detalle, idx) => {
                          const tipoIndex = chartData.findIndex(d => d.tipo === detalle.tipoNombre);
                          const tipoColor = tipoIndex >= 0 ? colors[tipoIndex % colors.length] : '#10B981';
                          return (
                            <div key={idx} className="bg-neutral-900 rounded-lg p-4 border border-gray-700">
                              <div className="flex items-center gap-2 font-bold text-white mb-3 font-mono">
                                <div 
                                  className="w-3 h-3 rounded" 
                                  style={{ backgroundColor: tipoColor, opacity: 0.8 }}
                                ></div>
                                <span className="text-green-400">{detalle.tipoNombre}</span>
                              </div>
                              <div className="space-y-2 text-sm font-mono">
                                <div className="flex justify-between text-gray-300">
                                  <span>Mínimo:</span>
                                  <span className="text-white font-bold">{detalle.minimo.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-300">
                                  <span>Máximo:</span>
                                  <span className="text-white font-bold">{detalle.maximo.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-300">
                                  <span>Umbral:</span>
                                  <span className="text-white font-bold">{detalle.umbral}</span>
                                </div>
                                <div className="flex justify-between text-gray-300">
                                  <span>Criticidad:</span>
                                  <span className="text-white font-bold">{detalle.criticidad}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {detallePorTipo.length === 0 && (
                        <div className="text-gray-400 font-mono text-center py-8">
                          Sin datos disponibles
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
        }

        // Lógica para comparación de múltiples lotes
        // Obtener todos los tipos únicos de todos los lotes
        const todosLosTipos = new Set<number>();
        lotesSeleccionados.forEach(lote => {
          Object.keys(lote.umbralesPorTipo).forEach(tipoid => {
            todosLosTipos.add(Number(tipoid));
          });
        });

        // Preparar datos del gráfico agrupados por tipo, con cada lote como una entrada
        const chartDataComparacion: Array<{
          tipo: string;
          [loteNombre: string]: string | number | null;
        }> = Array.from(todosLosTipos)
          .map(tipoid => {
            const tipo = tipos.find(t => t.tipoid === tipoid);
            const tipoNombre = tipo?.tipo || `Tipo ${tipoid}`;
            const entry: any = { tipo: tipoNombre };
            
            lotesSeleccionados.forEach(lote => {
              const umbralData = lote.umbralesPorTipo[tipoid];
              if (umbralData) {
                entry[`${lote.localizacion}_min`] = umbralData.minimo;
                entry[`${lote.localizacion}_max`] = umbralData.maximo;
                entry[`${lote.localizacion}_rango`] = umbralData.maximo - umbralData.minimo;
              } else {
                entry[`${lote.localizacion}_min`] = null;
                entry[`${lote.localizacion}_max`] = null;
                entry[`${lote.localizacion}_rango`] = null;
              }
            });
            
            return entry;
          })
          .sort((a, b) => a.tipo.localeCompare(b.tipo));

        // Colores para diferentes lotes
        const loteColors = [
          '#10B981', // verde
          '#3B82F6', // azul
          '#F59E0B', // amarillo
          '#EF4444', // rojo
          '#8B5CF6', // púrpura
          '#EC4899', // rosa
          '#06B6D4', // cyan
          '#84CC16', // lime
        ];

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-neutral-900 dark:bg-neutral-900 rounded-xl shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-600 dark:border-gray-600">
                <div>
                  <h2 className="text-xl font-bold text-white dark:text-white font-mono tracking-wider">
                    COMPARACIÓN DE UMBRALES - {lotesSeleccionados.length} {lotesSeleccionados.length === 1 ? 'LOCALIZACIÓN' : 'LOCALIZACIONES'}
                  </h2>
                  <p className="text-sm text-gray-400 dark:text-gray-400 font-mono mt-1">
                    Comparación de rangos de umbrales por tipo de sensor
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Contenido */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Gráfico de comparación */}
                {chartDataComparacion.length > 0 && (
                  <div className="mb-6">
                    <div className="h-96 w-full bg-neutral-800 rounded-lg p-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartDataComparacion}
                          layout="vertical"
                          margin={{ top: 10, right: 30, left: 150, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                          <XAxis 
                            type="number" 
                            domain={['auto', 'auto']}
                            stroke="#9CA3AF"
                            style={{ fontSize: '11px' }}
                            tick={{ fill: '#9CA3AF' }}
                          />
                          <YAxis 
                            type="category" 
                            dataKey="tipo" 
                            stroke="#9CA3AF"
                            style={{ fontSize: '11px' }}
                            tick={{ fill: '#9CA3AF' }}
                            width={140}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1F2937',
                              border: '1px solid #374151',
                              borderRadius: '8px',
                              color: '#F3F4F6',
                              padding: '8px 12px'
                            }}
                            cursor={{ 
                              fill: 'rgba(255, 255, 255, 0.1)',
                              stroke: 'rgba(16, 185, 129, 0.3)',
                              strokeWidth: 1
                            }}
                            content={({ active, payload }) => {
                              if (active && payload && payload.length > 0) {
                                const data = payload[0].payload;
                                // Filtrar solo las barras de rango (no las de min que son transparentes)
                                const rangos = payload.filter((p: any) => p.dataKey && p.dataKey.includes('_rango'));
                                const loteInfo = rangos.map((p: any) => {
                                  const loteName = p.dataKey.replace('_rango', '');
                                  const min = data[`${loteName}_min`];
                                  const max = data[`${loteName}_max`];
                                  if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
                                    // Encontrar el índice del lote para obtener su color
                                    const loteIndex = lotesSeleccionados.findIndex(l => l.localizacion === loteName);
                                    const loteColor = loteIndex >= 0 ? loteColors[loteIndex % loteColors.length] : '#10B981';
                                    return {
                                      name: loteName,
                                      min,
                                      max,
                                      color: loteColor
                                    };
                                  }
                                  return null;
                                }).filter(Boolean);
                                
                                if (loteInfo.length === 0) return null;
                                
                                return (
                                  <div className="font-mono text-sm space-y-1">
                                    {loteInfo.map((info: any, idx: number) => (
                                      <div key={idx} style={{ color: info.color }}>
                                        {info.name}: [{info.min.toFixed(2)} - {info.max.toFixed(2)}]
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          {lotesSeleccionados.map((lote, loteIndex) => {
                            const loteColor = loteColors[loteIndex % loteColors.length];
                            // Primero agregar la barra base (min) transparente para posicionar correctamente
                            return (
                              <React.Fragment key={lote.localizacionid}>
                                <Bar
                                  dataKey={`${lote.localizacion}_min`}
                                  stackId={`stack_${lote.localizacionid}`}
                                  fill="transparent"
                                />
                                <Bar
                                  dataKey={`${lote.localizacion}_rango`}
                                  stackId={`stack_${lote.localizacionid}`}
                                  fill={loteColor}
                                  fillOpacity={0.7}
                                  radius={[0, 4, 4, 0]}
                                >
                                  {chartDataComparacion.map((entry, index) => {
                                    const hasData = entry[`${lote.localizacion}_rango`] !== null;
                                    return (
                                      <Cell 
                                        key={`cell-${lote.localizacionid}-${index}`} 
                                        fill={hasData ? loteColor : 'transparent'} 
                                      />
                                    );
                                  })}
                                </Bar>
                              </React.Fragment>
                            );
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Leyenda con colores por lote */}
                    <div className="mt-4">
                      <div className="text-xs text-gray-400 dark:text-gray-400 font-mono mb-2 text-center">
                        Lotes comparados:
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-300 dark:text-gray-300 font-mono">
                        {lotesSeleccionados.map((lote, index) => (
                          <div key={lote.localizacionid} className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded" 
                              style={{ backgroundColor: loteColors[index % loteColors.length], opacity: 0.7 }}
                            ></div>
                            <span>{lote.localizacion}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default UmbralesPorLote;

